/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { useRef, useState } from 'react';

import { jsx, cssMap as unboundedCssMap } from '@compiled/react';

import IconButton from '@atlaskit/button/icon/button';
import { cssMap } from '@atlaskit/css';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronUpIcon from '@atlaskit/icon/core/chevron-up';
import { Box, Inline, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import {
	type HeaderElement,
	type ParsedElement,
} from '../../dynamic-layout-parser/types/dynamic-layout-section';
import { OneColumnLayout } from './section-layout/one-column-layout';

type FoldedStyleProps = {
	defaultFoldedState: 'collapsed' | 'expanded';
	header: HeaderElement | undefined;
	content: ParsedElement[];
	children?: React.ReactNode;
};

const unboundedStyles = unboundedCssMap({
	root: {
		marginTop: token('space.200'),
		width: '100%',
	},
	contentWrapper: {
		overflow: 'hidden',
		display: 'flex',
		flexDirection: 'column',
	},
	contentExpanded: {
		maxHeight: '500px',
	},
	contentCollapsed: {
		maxHeight: '64px',
		overflow: 'hidden',
		display: '-webkit-box',
		WebkitBoxOrient: 'vertical',
		WebkitLineClamp: 1,
	},
	pointer: {
		cursor: 'pointer',
	},
});

const styles = cssMap({
	divider: {
		borderTopStyle: 'solid',
		borderTopWidth: token('border.width'),
		borderTopColor: token('color.border'),
		marginTop: token('space.200'),
	},
	headerWrapper: {
		marginBottom: token('space.100'),
	},
});

export const DynamicLayoutFoldableSection = ({
	defaultFoldedState,
	header,
	content,
	children,
}: FoldedStyleProps): React.JSX.Element => {
	const containerRef = useRef(null);
	const [isFolded, setIsFolded] = useState<boolean>(defaultFoldedState === 'collapsed');
	const sectionKey = `folded-section-${header?.children || ''}`;

	const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
		if ((event.key === 'Enter' || event.key === ' ') && isFolded) {
			event.preventDefault();
			setIsFolded(false);
		}
	};

	return (
		<div
			css={[unboundedStyles.root, isFolded && unboundedStyles.pointer]}
			data-testid="dynamic-layout-foldable-section"
		>
			<Inline>
				<IconButton
					icon={isFolded ? ChevronDownIcon : ChevronUpIcon}
					label={`${isFolded ? 'expand' : 'collapsed'} section`}
					appearance="subtle"
					spacing="compact"
					onClick={() => setIsFolded(!isFolded)}
					testId={isFolded ? 'expand-button' : 'collapse-button'}
					aria-expanded={!isFolded}
				/>
				<div
					ref={containerRef}
					css={[
						unboundedStyles.contentWrapper,
						isFolded ? unboundedStyles.contentCollapsed : unboundedStyles.contentExpanded,
					]}
					onClick={() => setIsFolded(false)}
					onKeyDown={handleKeyDown}
					tabIndex={0}
					role="button"
					data-testid="dynamic-layout-foldable-clickable-section"
				>
					<Inline alignBlock="center" xcss={styles.headerWrapper}>
						{header && header.attributes?.show !== 'false' && content && (
							<Text weight="bold">{header && (header.children as string)}</Text>
						)}
					</Inline>
					{isFolded ? (
						<OneColumnLayout key={`${sectionKey}-collapsed`} elements={content} />
					) : (
						<Box key={`${sectionKey}-expanded`}>{children}</Box>
					)}
				</div>
			</Inline>
			<Box xcss={styles.divider} />
		</div>
	);
};
